import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Media query for desktop breakpoint
   * @type {MediaQueryList}
   */
  #desktopMediaQuery;

  connectedCallback() {
    super.connectedCallback();

    // Get desktop breakpoint from data attribute (default to 1140px)
    const breakpoint = this.dataset.desktopBreakpoint || '1140';
    this.#desktopMediaQuery = window.matchMedia(`(min-width: ${breakpoint}px)`);
    
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Initialize the current slide
    this.#initializeSlides();
    
    // Check initial state and set attribute
    this.#updateDisplayMode();
    
    // Listen for breakpoint changes
    this.#desktopMediaQuery.addEventListener('change', this.#handleBreakpointChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#desktopMediaQuery?.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Initialize slides - ensure at least one slide is visible
   */
  #initializeSlides() {
    if (!this.refs.slides?.length) return;
    
    // Set initial slide visibility
    this.current = 0;
  }

  /**
   * Handle breakpoint changes
   */
  #handleBreakpointChange = () => {
    this.#updateDisplayMode();
  };

  /**
   * Update display mode based on screen size
   */
  #updateDisplayMode() {
    if (this.#desktopMediaQuery.matches) {
      // Desktop: also use slider behavior
      this.removeAttribute('data-desktop-static');
      this.#updateSlideVisibility();
      this.resume(); // Resume autoplay on desktop too
    } else {
      // Mobile/Tablet: slider behavior
      this.removeAttribute('data-desktop-static');
      this.#updateSlideVisibility();
      this.resume(); // Resume autoplay on mobile/tablet
    }
  }

  /**
   * Update slide visibility for slider mode
   */
  #updateSlideVisibility() {
    if (!this.refs.slides?.length) return;
    
    let relativeIndex = this.#current % this.refs.slides.length;
    if (relativeIndex < 0) {
      relativeIndex += this.refs.slides.length;
    }

    this.refs.slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    // Always update slide visibility, but the CSS will handle the display
    this.#updateSlideVisibility();
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
